/**
 * Preprocesses markdown content to normalize LaTeX formula syntax so the
 * downstream pipeline (remark-math / KaTeX / Pandoc) renders it as real math.
 *
 * Handles:
 *  - ChatGPT delimiters: \( ... \) → $ ... $   and   \[ ... \] → $$ ... $$
 *  - Currency: escapes "$5", "$10.99" so they aren't parsed as inline math
 *  - Legacy heuristics: bare "[ ... ]" → $$ and "( ... )" → $ for content that
 *    has no $-delimiters at all (kept for back-compat with older paste styles)
 *
 * The function is idempotent — running it twice yields the same result, which
 * matters because some callers (e.g. the client DOCX converter) preprocess
 * input that the UI already preprocessed.
 */
export function preprocessMarkdown(markdown: string): string {
  let result = markdown;

  // 0-. A table written one paragraph per cell, as a rich-text → Markdown
  //     converter writes a table copied from a chat page (#66). Rebuilt
  //     before anything reads pipe rows.
  result = rebuildCellPerParagraphTables(result);
  //     Pipe-table rows broken across lines, as a chat export writes them
  //     when a cell ends with a blank line (#52). Independent of math, so it
  //     runs first; a joined row is a complete row, so it never runs twice.
  result = joinBrokenTableRows(result);
  //     A table written straight under a paragraph line, no blank line
  //     between (#57/#58): the preview shows a table, Pandoc shows pipes.
  result = separateTableFromParagraph(result);

  // 0. Markdown-escaped math. A chat client's copy button (seen from Gemini)
  //    can escape the reply as CommonMark, so inside $…$ every backslash is
  //    doubled ("\\frac") and brackets read "\[": KaTeX shows "sqrt" as
  //    letters and Pandoc leaves raw TeX. Undo it while the spans are intact,
  //    before step 1 mistakes an escaped "\[" inside math for a delimiter.
  result = unescapeMarkdownEscapedMath(result);

  // 0a. A closing dollar carried to the start of the next line (#70). Pandoc
  //     closes `$` only after a non-space, and a newline is one; the stray
  //     dollar also paired every later `$` on its line off by one.
  result = joinLineBrokenClosingDollar(result);

  // 0b. Inline math padded with spaces inside the dollars, "$ m^{3}/d $", as
  //     OCR tools write it. remark-math renders it; Pandoc does not (#45).
  result = tightenSpacedInlineMath(result);

  // 0c. ChatGPT delimiters escaped as CommonMark ("\\[ … \\]", "\\_"), as a
  //     copy-as-Markdown serializer writes them (#46). Unescape, then step 1.
  const escapedDelimiters = hasEscapedDelimiters(result);
  result = unescapeEscapedDelimiters(result);

  // 1. Normalize ChatGPT's backslash delimiters to standard $ / $$.
  result = convertChatGptDelimiters(result);

  // 1'. The same serializer's `$` spans — after step 1, so spans it has just
  //     made from "\( x\_1 \)" count too; before it, they were found only on
  //     a second pass, which broke idempotency (2026-10-15).
  result = unescapeSerializerDollarSpans(result, escapedDelimiters);

  // 1a. LaTeX pasted "on its own": \begin{equation|align|gather|…} blocks
  //     and bare formula lines carry no $ delimiters, so they used to pass
  //     through as raw text — on /latex-to-word, of all places (2026-09-20:
  //     44 visitors pasted there, 26 never exported). Environments are also
  //     renamed to the forms BOTH engines render (KaTeX has no multline,
  //     eqnarray, flalign, or \label; texmath has no such gaps but needs $$).
  const beforeBareLatex = result;
  result = normalizeLatexEnvironments(result);
  result = wrapBareFormulaLines(result, { strongOnly: true });
  const addedFences = result !== beforeBareLatex;

  // 1b. Put multi-line $$…$$ fences on their own lines — remark-math cannot
  //     parse them otherwise, while Pandoc can (issue #26: the preview showed
  //     raw red LaTeX for a document whose DOCX math was actually fine).
  result = normalizeDisplayMath(result);

  // 1d. A delimiter followed by an unescaped brace, "\left{ … \right}", which
  //     KaTeX and texmath both reject (#49). The brace is escaped; nothing
  //     else about the formula is guessed.
  result = repairDelimiterBraces(result);

  // 1c. Terminal box-drawing tables (┌─┬─┐ …) become real markdown tables.
  //     CLI-formatted AI answers (Claude Code and friends) draw tables with
  //     Unicode box characters; as markdown they are just prose lines, so
  //     every export renders them as wrapped paragraph soup (issues #27/#29).
  result = convertBoxTables(result);

  // 2. Separate back-to-back links so they don't read as one merged word.
  result = spaceAdjacentLinks(result);

  // 3. Escape currency amounts ($5, $1,000.50) so remark-math doesn't pair
  //    two prices into a bogus formula.
  result = escapeCurrency(result);

  // 3a. The same padding on one side only, "$C_{a} = $ 40 per part" (#67).
  //     After step 3, so a price's dollar is escaped by now and cannot be
  //     taken for the closing one.
  result = tightenSpacedInlineMath(result, { eitherSide: true });

  // 4. If real $-math is now present, the aggressive ( )/[ ] heuristics below
  //    would mangle prose — so stop here. Fences that step 1a added do not
  //    count: a Word-paste document with one bare \frac line still needs its
  //    "(v) = velocity" definitions converted, so the gate reads the text as
  //    it would have been without 1a.
  const gateText = addedFences
    ? escapeCurrency(spaceAdjacentLinks(convertBoxTables(normalizeDisplayMath(beforeBareLatex))))
    : result;
  if (hasDollarMath(gateText)) {
    return result;
  }

  // 5. Legacy heuristics for pastes that use bare brackets/parens for math.
  //    With no $ anywhere, a bare line like "a^2 + b^2 = c^2" is math too.
  result = wrapBareFormulaLines(result, { strongOnly: false });
  result = convertBlockFormulas(result);
  result = convertInlineFormulas(result);
  // Last: LaTeX fragments inside running text, "… is \frac{3}{4} \times
  // 100 = 75\% of …", which the line and bracket rules above cannot see.
  result = wrapInlineBareMath(result);

  return result;
}

// A command as a chat model writes it, with its backslash doubled. When every
// command in a math span looks like this, the span was markdown-escaped on
// its way here — nobody types "\\frac" by hand.
const DOUBLED_COMMAND =
  /\\\\(?:d?frac|tfrac|sqrt|text|left|right|cdot|times|sum|prod|int|lim|ln|log|sin|cos|tan|cot|csc|sec|alpha|beta|gamma|delta|theta|lambda|mu|pi|sigma|omega|phi|approx|le|ge|leq|geq|neq|pm|infty|partial|quad|mathbf|mathrm|mathbb|hat|bar|vec|begin|end)(?![a-zA-Z])/;
// A command with a single backslash — its presence says the span is genuine TeX.
const SINGLE_COMMAND = /(?<!\\)\\(?!\\)[a-zA-Z]/;
// $$…$$ (any lines) or $…$ (one line; not an escaped \$, not part of $$).
const MATH_SPAN = /\$\$[\s\S]*?\$\$|(?<![\\$])\$(?!\$)[^$\n]+?\$(?!\$)/g;

/**
 * Undo CommonMark escaping inside math spans. A span whose commands are all
 * doubled ("\\frac", "\\sqrt") gets `\\` → `\`, `\_` → `_`, `\*` → `*`;
 * escaped brackets `\[` `\]` become `[` `]` in any math span, since `\[` is
 * never valid inside math and step 1 would otherwise read it as a display
 * delimiter (a product rule written "\[uv\]" came out as "$uv$"). Prose,
 * currency, and genuine TeX (single-backslash commands) are left alone.
 *
 * The copy button escapes the whole reply, so once one span is recognisably
 * escaped, every span without genuine TeX is treated as escaped too — even
 * one with no listed command: "$P\_1 P\_2$" (a literal underscore in TeX) or
 * "$x\_1\\vert{}y\_1$" (raw text in Word, since "\\vert" reads as a line
 * break). Issue #43: the display formulas converted, the inline ones did not.
 * In such a document a span with no command at all is escaped too: "$\\{a,
 * b\\}$" has only a doubled brace (found by the escaping round-trip test,
 * 2026-10-15). Idempotent: the output has no doubled commands and no escaped
 * brackets.
 */
function unescapeMarkdownEscapedMath(text: string): string {
  if (!text.includes("$") || !text.includes("\\")) return text;
  let escapedDoc = false;
  outsideCodeFences(text, (chunk) => {
    escapedDoc ||= (chunk.match(MATH_SPAN) ?? []).some(
      (span) => DOUBLED_COMMAND.test(span) && !SINGLE_COMMAND.test(span),
    );
    return chunk;
  });
  return outsideCodeFences(text, (chunk) =>
    chunk.replace(MATH_SPAN, (span) => {
      const doubled =
        !SINGLE_COMMAND.test(span) &&
        (DOUBLED_COMMAND.test(span) || (escapedDoc && /\\\\|\\[_*<>]/.test(span)));
      // A bracket escape is a single backslash: in "\\\\[2pt]" (a TeX line
      // break with spacing) the backslash before "[" is itself escaped.
      const escapedBrackets = /(?<!\\)\\[[\]]/.test(span);
      if (!doubled && !escapedBrackets) return span;
      if (!doubled && !looksLikeMathSpan(span.replace(/^\$+|\$+$/g, ""))) return span;
      let out = span;
      if (doubled) out = out.replace(/\\\\/g, "\\").replace(/\\([_*<>])/g, "$1");
      return out.replace(/(?<!\\)\\([[\]])/g, "$1");
    }),
  );
}

/**
 * ChatGPT-style delimiters escaped as CommonMark: `\\[ F=P\_{\mathrm{ext}}A \\]`.
 *
 * A Markdown serializer keeps a backslash that precedes punctuation by
 * doubling it, and escapes `_`, `<`, `*` where they could mean something, so
 * TeX comes out as `\\[`, `\\,`, `\_`, `\<` — while `\mathrm`, a backslash
 * before a letter, stays single. Rendering that as CommonMark gives the TeX
 * back; this preprocessor reads the raw text, so step 1 matched `\[` one
 * backslash in and left the other, and Word got stray dollars and raw TeX
 * (issue #46, 2026-10-12: 17 equations out of about 51). Each escaped span is
 * unescaped the CommonMark way — a backslash before ASCII punctuation is
 * dropped, so `\\,` → `\,` and `\_` → `_` but `\mathrm` stays — and step 1
 * then converts it like any `\[ … \]`. Exactly two backslashes open and close
 * a span (three or four would be an escaped backslash or a TeX line break);
 * a display span stays within its paragraph, an inline one within its line.
 * Code is untouched.
 */
const ESCAPED_DELIMITED =
  /(?<!\\)\\\\\[(?:(?!\n[ \t]*\n)[\s\S])*?(?<!\\)\\\\\]|(?<!\\)\\\\\([^\n]*?(?<!\\)\\\\\)/g;

// A `$` span that came through the same serializer: something of its doing
// to undo — a doubled run before punctuation, or its own escape of _ < > * —
// and no lone backslash before any other punctuation, which genuine TeX has
// ("\{", "\%", "\,") and that serializer never leaves single.
// "-", "+", "(" and "[" are left out: inside a $ span, two backslashes
// before them are a TeX row break before the next row's first entry
// ("2&-1\\-1&2", "1\\(a+b)"), which the serializer would have written with
// four; TeX has no "\-", "\+", and no "\(" or "\[" inside math. Counting
// them made a matrix-heavy document "escaped" (#60).
const SERIALIZER_UNDO = /\\\\[!-')*,./:-@\\\]-`{-~]|\\[_<>*]/;
const GENUINE_ESCAPE = /(?<!\\)\\(?!\\)[!"#$%&'()+,\-./:;=?@\\^`{|}~]/;

/**
 * The same display delimiters through the Gemini-copy-button style (model A
 * in tests/escaping-roundtrip.test.ts: every backslash doubled, `_ * [ ]`
 * escaped with one more): `\[` comes out as `\\\[` — three backslashes, an
 * escaped backslash and an escaped bracket — which the pattern above leaves
 * alone on purpose (issue #66, 2026-10-30: the display formula of a
 * \( \[-delimited answer reached Word as raw TeX while its inline formulas,
 * whose "(" that style does not escape, converted). Taken only when every
 * backslash in the span fits that style — runs before letters even, odd runs
 * only before `_ * [ ]` — and what it unescapes to reads as math, so a prose
 * mention of the delimiters ("write \\\[ and \\\] around it") stays.
 */
const ESCAPED_TRIPLE_DISPLAY = /(?<!\\)\\\\\\\[(?:(?!\n[ \t]*\n)[\s\S])*?(?<!\\)\\\\\\\]/g;
function copyButtonEscapedDisplay(span: string): boolean {
  for (const m of span.matchAll(/(\\+)([\s\S]?)/g)) {
    if (m[1].length % 2 === 1 && !/[_*[\]]/.test(m[2])) return false;
  }
  const inner = unescapeTexSpan(span).replace(/^\\\[|\\\]$/g, "").trim();
  return looksLikeMathSpan(inner);
}

const hasEscapedDelimiters = (text: string): boolean =>
  ((text.includes("\\\\[") || text.includes("\\\\(")) && new RegExp(ESCAPED_DELIMITED.source).test(text)) ||
  (text.includes("\\\\\\[") && (text.match(new RegExp(ESCAPED_TRIPLE_DISPLAY.source, "g")) ?? []).some(copyButtonEscapedDisplay));

function unescapeEscapedDelimiters(text: string): string {
  // A matched pair, not a bare "\\[": display math writes a row break with
  // spacing as "\\[4pt]", and that must not make a document "escaped".
  if (!hasEscapedDelimiters(text)) return text;
  return outsideCodeFences(text, (chunk) =>
    chunk
      .split(/(`+[^`\n]*?`+)/)
      .map((part, i) =>
        i % 2 === 1
          ? part
          : part
              .replace(ESCAPED_TRIPLE_DISPLAY, (span) => (copyButtonEscapedDisplay(span) ? unescapeTexSpan(span) : span))
              .replace(ESCAPED_DELIMITED, (span) => unescapeTexSpan(span)),
      )
      .join(""),
  );
}

/**
 * The same serializer escapes `$…$` and `$$…$$` spans the same way (a Gemini,
 * Claude, or DeepSeek answer copied through it): "$P\_{ext}$", "\<", "\\,".
 * Evidence that it was at work: an escaped delimiter pair anywhere in the
 * document (`delimited`), or at least two spans that carry its marks and
 * none of TeX's own single escapes — one such span alone ("$file\_name$") may
 * be a deliberate literal underscore and stays. Found by the escaping
 * round-trip test (2026-10-15) before any report.
 */
function unescapeSerializerDollarSpans(text: string, delimited: boolean): string {
  if (!/\\\\[^\sa-zA-Z\\]|\\[_<>*]/.test(text)) return text;
  const marked = (span: string) => SERIALIZER_UNDO.test(span) && !GENUINE_ESCAPE.test(span);
  if (!delimited) {
    let markedSpans = 0;
    outsideCodeFences(text, (chunk) => {
      for (const span of chunk.match(MATH_SPAN) ?? []) if (marked(span)) markedSpans++;
      return chunk;
    });
    if (markedSpans < 2) return text;
  }
  return outsideCodeFences(text, (chunk) =>
    chunk
      .split(/(`+[^`\n]*?`+)/)
      .map((part, i) => (i % 2 === 1 ? part : part.replace(MATH_SPAN, (span) => (marked(span) ? unescapeTexSpan(span, true) : span))))
      .join(""),
  );
}

/**
 * Unescape one escaped span, run of backslashes by run. The serializers seen
 * so far escape a backslash only before punctuation, so:
 *   - before ASCII punctuation, a run of N becomes ⌊N/2⌋ (CommonMark):
 *     `\\[` → `\[`, `\\,` → `\,`, `\_` → `_`;
 *   - two before a space or the end are a TeX row break the serializer left
 *     alone (`a & b \\ c & d`) and stay — collapsing them turned every
 *     matrix in issue #48 (2026-10-15) into a single row; four become two;
 *   - two or more before a TeX length in brackets (`\\[4pt]`) are a row
 *     break with spacing and come out as two;
 *   - exactly two before "-" or "+" are a row break before a signed entry
 *     (`2&-1\\-1&2`) and stay: the serializer writes that break with four,
 *     and a TeX "\-" (discretionary hyphen) never occurs in math — halving
 *     them flattened two matrices of issue #60 (2026-10-26) into one row;
 *     in a $ span (`inMath`) the same holds before "(" and "[", where a
 *     halved "\(" or "\[" is a delimiter KaTeX rejects inside math (in an
 *     escaped-delimiter span those are the span's own delimiters and halve);
 *   - before a letter (`\mathrm`) the run is a command and stays.
 */
const TEX_LENGTH = /^\[\s*-?\d+(?:\.\d+)?\s*(?:pt|em|ex|mm|cm|in|mu)\s*\]/;
function unescapeTexSpan(span: string, inMath = false): string {
  // Every command doubled and none single: this span's serializer doubled
  // every backslash (the Gemini copy button's style, #43), so every run
  // halves — before letters too — and a lone backslash before _ * [ ] is the
  // serializer's own escape, which halving to none undoes as well.
  const allDoubled = /\\\\[a-zA-Z]/.test(span) && !SINGLE_COMMAND.test(span);
  return span.replace(/(\\+)([\s\S]?)/g, (_m, run: string, next: string, at: number) => {
    const n = run.length;
    if (allDoubled) return "\\".repeat(Math.floor(n / 2)) + next;
    if (n >= 2 && next === "[" && TEX_LENGTH.test(span.slice(at + n))) return "\\\\" + next;
    if (n === 2 && (next === "-" || next === "+")) return run + next;
    if (inMath && n === 2 && (next === "(" || next === "[")) return run + next;
    if (/[!-/:-@[-`{-~]/.test(next)) return "\\".repeat(Math.floor(n / 2)) + next;
    if (next === "" || /\s/.test(next)) return (n >= 4 ? "\\".repeat(Math.floor(n / 2)) : run) + next;
    return run + next;
  });
}

/**
 * Drop the spaces just inside `$ … $` when the content is clearly math.
 *
 * OCR tools (PaddleOCR's Markdown output) write every inline formula as
 * "$ m^{3}/d $". remark-math renders that, but Pandoc opens `$` only before a
 * non-space and closes it only after one, so the preview showed math while
 * Word received the raw TeX, dollars and all (issue #45, 2026-10-10: 16
 * formulas, none an equation in the file). The span must be padded on both
 * sides and its content must carry a command, `=`, `^`, or a subscript, or be
 * a single letter — "$ 5 and $ 10" and "$5 _each_ and $10" stay text. Code spans and fences are untouched; `$$` displays need no help
 * (Pandoc allows the spaces there). Idempotent.
 *
 * With `eitherSide`, a span padded on one side only is tightened too:
 * "Given: $C_{a} = $ 40 per part" leaves the space before the closing
 * dollar, and Pandoc wrote all four such inputs of a worked example as text
 * (issue #67, 2026-11-01: 14 of 116 formulas). That pass runs after the
 * currency escaping, when no price's dollar is left to pair with. A trailing
 * TeX control space ("$a\ $") is content, not padding, and is kept.
 */
/**
 * A closing dollar carried to the start of the next line,
 *
 *     … has an inverse if $A\cdot A^{-1}
 *     $ such that …
 *
 * remark-math closes the span across the line break; Pandoc closes `$` only
 * after a non-space, and a newline is one, so the formula reached Word as
 * text (#70, 2026-11-03: 3 of 99). The stray dollar also made every later
 * `$` on its line pair off by one, which hid a padded span there from the
 * rule in step 3a. The dollar moves back to the end of the formula; the line
 * keeps its break. Only when the line above leaves a span open — an odd
 * number of single dollars outside code — whose content starts without a
 * space and passes the math test, and the dollar below starts neither a `$$`
 * display nor a price. Lines are read top down, so a line this closes counts
 * as closed for the one after it. Fenced code is never entered.
 */
function joinLineBrokenClosingDollar(text: string): string {
  if (!/\n\$(?![$\d])/.test(text)) return text;
  return outsideCodeFences(text, (chunk) => {
    const lines = chunk.split("\n");
    for (let i = 0; i + 1 < lines.length; i++) {
      if (!/^\$(?![$\d])/.test(lines[i + 1])) continue;
      if (openMathSpan(lines[i]) === null) continue;
      lines[i] = `${lines[i].trimEnd()}$`;
      const rest = lines[i + 1].slice(1);
      if (rest.trim()) lines[i + 1] = rest;
      else lines.splice(i + 1, 1);
    }
    return lines.join("\n");
  });
}

/** The content of the `$` span a line leaves open, when it reads as math; null when the line is balanced or the tail is prose. */
function openMathSpan(line: string): string | null {
  const code = codeSpanRanges(line);
  const DOLLAR = /(?<![\\$])\$(?!\$)/g;
  const dollars: number[] = [];
  let m: RegExpExecArray | null;
  while ((m = DOLLAR.exec(line)) !== null) {
    const at = m.index;
    if (!code.some(([s, e]) => at >= s && at < e)) dollars.push(at);
  }
  if (dollars.length % 2 === 0) return null;
  const inner = line.slice(dollars[dollars.length - 1] + 1).trimEnd();
  if (!inner || /^[ \t]/.test(inner) || inner.endsWith("\\")) return null;
  return looksLikeMathSpan(inner) ? inner : null;
}

function tightenSpacedInlineMath(text: string, { eitherSide = false } = {}): string {
  if (!/\$[ \t]|[ \t]\$/.test(text)) return text;
  return outsideCodeFences(text, (chunk) =>
    chunk
      .split("\n")
      .map((line) => {
        if (!line.includes("$")) return line;
        const code = codeSpanRanges(line);
        return line.replace(/(?<![\\$])\$(?!\$)([^$\n]+?)(?<!\\)\$(?!\$)/g, (span, inner: string, at: number) => {
          if (code.some(([s, e]) => at >= s && at < e)) return span;
          // Before the currency pass, padded on both sides, as OCR writes it:
          // "$5 _each_ and $10" is two prices around emphasis, not a span.
          const lead = /^[ \t]/.test(inner);
          const trail = /[ \t]$/.test(inner);
          if (eitherSide ? !lead && !trail : !lead || !trail) return span;
          const trimmed = inner.trim();
          if (!trimmed || trimmed.endsWith("\\")) return span;
          const mathy =
            looksLikeMathSpan(trimmed) ||
            /[A-Za-z0-9}]_[{A-Za-z0-9]/.test(trimmed) || // a subscript, not _emphasis_
            /^[A-Za-z\u0370-\u03ff]$/.test(trimmed);
          return mathy ? `$${trimmed}$` : span;
        });
      })
      .join("\n"),
  );
}

/**
 * Convert ChatGPT/KaTeX backslash delimiters to standard dollar delimiters.
 * Block \[ ... \] becomes $$ on its own lines; inline \( ... \) becomes $...$.
 * Inner whitespace is trimmed so remark-math reliably opens/closes the span.
 * Code is literal: until 2026-10-13 a LaTeX example inside a code block or an
 * inline code span ("```latex\n\[ x^2 \]\n```", "`\( x \)`") was rewritten
 * to dollars too, changing the very code the answer was showing.
 */
function convertChatGptDelimiters(text: string): string {
  return outsideCodeFences(text, (chunk) =>
    chunk
      .split(/(`+[^`\n]*?`+)/)
      .map((part, i) => (i % 2 === 1 ? part : convertDelimitersIn(part)))
      .join(""),
  );
}

function convertDelimitersIn(text: string): string {
  return text
    .replace(/\\\[\s*([\s\S]*?)\s*\\\]/g, (m, inner: string, offset: number, whole: string) => {
      // A \[…\] alone on its line(s) is a display block. Mid-sentence —
      // "Distance covered: \[ d = vt \] where…" — a block would split the
      // paragraph (or list item) in three, so it becomes inline math instead.
      const lineStart = whole.lastIndexOf("\n", offset - 1) + 1;
      const lineEndIdx = whole.indexOf("\n", offset + m.length);
      const before = whole.slice(lineStart, offset).replace(/^\s*(?:[-*+]|\d+[.)])?\s*/, "");
      const after = whole.slice(offset + m.length, lineEndIdx < 0 ? whole.length : lineEndIdx).trim();
      const alone = before === "" && after === "";
      if (alone || inner.includes("\n")) return `$$\n${inner}\n$$`;
      return `$${inner}$`;
    })
    .replace(/\\\(\s*([\s\S]*?)\s*\\\)/g, (_m, inner) => `$${inner}$`);
}

const CODE_FENCE_RE = /^\s*(?:```|~~~)/;

/** Run `fn` over the text outside fenced code; fences pass through untouched. */
function outsideCodeFences(text: string, fn: (chunk: string) => string): string {
  const out: string[] = [];
  let buffer: string[] = [];
  let inFence = false;
  const flush = () => {
    if (buffer.length) out.push(fn(buffer.join("\n")));
    buffer = [];
  };
  for (const line of text.split("\n")) {
    if (CODE_FENCE_RE.test(line)) {
      if (!inFence) flush();
      inFence = !inFence;
      out.push(line);
    } else if (inFence) {
      out.push(line);
    } else {
      buffer.push(line);
    }
  }
  flush();
  return out.join("\n");
}

// Display environments people paste, mapped to what KaTeX AND Pandoc's texmath
// both render (checked against KaTeX 0.16 and Pandoc 3.11 on 2026-09-20):
// KaTeX has no multline / eqnarray / flalign; both engines take aligned and
// gathered; a plain body needs no environment at all.
const LATEX_ENV: Record<string, "plain" | "aligned" | "gathered" | "inline"> = {
  equation: "plain", displaymath: "plain",
  align: "aligned", alignat: "aligned", flalign: "aligned", eqnarray: "aligned",
  gather: "gathered", multline: "gathered",
  math: "inline",
};
const LATEX_ENV_RE =
  /\\begin\{(equation|displaymath|align|alignat|flalign|eqnarray|gather|multline|math)(\*?)\}(\{\d+\})?([\s\S]*?)\\end\{\1\2\}/g;

/**
 * `\begin{equation}…\end{equation}` and friends → display math both engines
 * render. Outside `$$` the block gains its own `$$` fences; inside existing
 * math only the environment is renamed. `\label{…}` goes (KaTeX rejects it,
 * and a cross-reference has nowhere to point in Word). eqnarray's `&=&`
 * becomes aligned's `&=`. Fenced code is never entered. Idempotent: the output
 * contains none of the source environments.
 */
function normalizeLatexEnvironments(text: string): string {
  if (!text.includes("\\begin{")) return text;
  return outsideCodeFences(text, (chunk) =>
    chunk.replace(LATEX_ENV_RE, (match, env: string, _star: string, _arg: string | undefined, inner: string, offset: number) => {
      const kind = LATEX_ENV[env];
      let body = inner.replace(/\\label\{[^}]*\}/g, "").trim();
      if (!body) return match;
      if (env === "eqnarray") body = body.replace(/&\s*(=|<|>|\\[a-zA-Z]+)\s*&/g, "&$1");
      const before = chunk.slice(0, offset);
      if (kind === "inline") return /\$\s*$/.test(before) ? body : `$${body}$`;
      const math = kind === "plain" ? body : `\\begin{${kind}}\n${body}\n\\end{${kind}}`;
      // An odd number of $$ before this point means we are already in display math.
      const insideDisplay = (before.match(/(?<!\\)\$\$/g) ?? []).length % 2 === 1;
      return insideDisplay ? math : `\n\n$$\n${math}\n$$\n\n`;
    }).replace(/\n{3,}/g, "\n\n"),
  );
}

const STRONG_MATH_COMMAND =
  /\\(?:d?frac|tfrac|sqrt|sum|prod|int|iint|oint|lim|partial|nabla|infty|cdot|times|leq|geq|neq|approx|pm|binom|left|right|alpha|beta|gamma|delta|epsilon|varepsilon|theta|lambda|mu|pi|sigma|omega|phi|varphi|psi|rho|tau|Delta|Sigma|Omega|Gamma|Lambda|Phi|Psi|mathbf|mathrm|mathbb|mathcal|hat|bar|vec)(?![a-zA-Z])/;

/**
 * A line that is nothing but a formula — `\frac{a}{b} = \sqrt{c}` — becomes a
 * display block. Strict on purpose: the line must carry a math command (or,
 * when the document has no `$` at all and `strongOnly` is off, a `^`/`_` with
 * an `=`), must not be Markdown structure, a table row, or already delimited,
 * and must contain no prose: once commands and `\text{…}` groups are removed,
 * no word of four or more letters may remain. Sentences that merely mention a
 * command stay sentences; snake_case identifiers stay text.
 */
function wrapBareFormulaLines(text: string, opts: { strongOnly: boolean }): string {
  if (!/[\\^_]/.test(text)) return text;
  return outsideCodeFences(text, (chunk) => {
    let inDisplay = false;
    let inBracketBlock = false; // a lone "[" … "]" block belongs to convertBlockFormulas
    return chunk
      .split("\n")
      .map((line) => {
        const trimmed = line.trim();
        if (!inDisplay && (trimmed === "[" || trimmed === "]")) {
          inBracketBlock = trimmed === "[";
          return line;
        }
        if (inBracketBlock) return line;
        // Track display math by every $$ on the line, not only lone fences:
        // this pass runs before the fences are normalised, and a formula line
        // inside "$$ a =" … "+ b $$" must never gain fences of its own.
        const fences = (trimmed.match(/(?<!\\)\$\$/g) ?? []).length;
        if (fences > 0) {
          if (fences % 2 === 1) inDisplay = !inDisplay;
          return line;
        }
        if (inDisplay || !trimmed) return line;
        // A table row starts with a pipe or spaces its pipes; "|H|" in a formula
        // is an absolute value and must not disqualify the line (#53).
        if (/[$`]/.test(trimmed) || /^\||\s\|\s/.test(trimmed)) return line;
        if (/^(?:#{1,6}\s|>|[-*+]\s|\d+[.)]\s|\\begin\{|\\end\{|\\\[|\\\(|<)/.test(trimmed)) return line;
        const strong = STRONG_MATH_COMMAND.test(trimmed);
        const weak = !opts.strongOnly && /[\^_]/.test(trimmed) && /=/.test(trimmed);
        if (!strong && !weak) return line;
        const residue = trimmed
          .replace(/\\(?:text|mathrm|operatorname|textbf|textit|mbox)\{[^}]*\}/g, " ")
          .replace(/\\[a-zA-Z]+/g, " ");
        if (/[A-Za-z]{4,}/.test(residue)) return line;
        const body = trimmed.replace(/\\\\\s*$/, "").trim();
        return body ? `$$\n${body}\n$$` : line;
      })
      .join("\n");
  });
}

// Commands that make a run of text math, with how many {…} arguments each
// takes. An anchor is only accepted when its arguments look like math (see
// mathLikeArgument), so prose that names a command stays prose.
const INLINE_ANCHORS: Record<string, number> = {
  frac: 2, dfrac: 2, tfrac: 2, sqrt: 1, text: 1, mathrm: 1, textbf: 1,
  times: 0, cdot: 0, div: 0, pm: 0, mp: 0, approx: 0, neq: 0, ne: 0, leq: 0, le: 0, geq: 0, ge: 0,
  rightarrow: 0, leftarrow: 0, Rightarrow: 0, Leftarrow: 0, leftrightarrow: 0, to: 0, infty: 0,
  alpha: 0, beta: 0, gamma: 0, delta: 0, Delta: 0, theta: 0, lambda: 0, mu: 0, pi: 0, sigma: 0,
  Sigma: 0, omega: 0, Omega: 0, phi: 0, rho: 0, tau: 0, epsilon: 0, circ: 0, degree: 0,
};
const TEXTUAL_COMMANDS = new Set(["text", "mathrm", "textbf"]);
// A symbol standing alone in a sentence ("\alpha is the learning rate") may be
// prose about the command; it is wrapped only as part of a longer run.
const LONE_SYMBOL = /^\\(?:alpha|beta|gamma|delta|Delta|theta|lambda|mu|pi|sigma|Sigma|omega|Omega|phi|rho|tau|epsilon|infty|circ|degree)$/;
const OPERATOR_CHARS = "+-=*/^_<>";

/** Index just past the {…} group starting at `i`, or -1. */
function braceGroupEnd(s: string, i: number): number {
  if (s[i] !== "{") return -1;
  let depth = 0;
  for (let k = i; k < s.length; k++) {
    if (s[k] === "{") depth++;
    else if (s[k] === "}" && --depth === 0) return k + 1;
    else if (s[k] === "\n") return -1;
  }
  return -1;
}

/** "{12}" yes, "{x+1}" yes, "{\text{cost}}" yes; "{numerator}" no — a word is not math. */
function mathLikeArgument(group: string): boolean {
  const inner = group.slice(1, -1).replace(/\\(?:text|mathrm|textbf)\{[^{}]*\}/g, " ");
  return inner.trim().length > 0 && !/[A-Za-z]{2,}/.test(inner.replace(/\\[A-Za-z]+/g, " "));
}

/**
 * A command at `i` ("\frac{3}{4}", "\times", "\%"): the index past it and
 * whether it counts as math, or null when `i` holds no command we know.
 */
function readCommand(
  s: string,
  i: number,
): { end: number; math: boolean; textual: boolean; continuationOnly?: boolean } | null {
  if (s[i] !== "\\") return null;
  // "75\%" continues a run but never starts one: Markdown already shows a
  // lone "\%" as "%", so wrapping it alone would change nothing but the font.
  if (s[i + 1] === "%") return { end: i + 2, math: true, textual: false, continuationOnly: true };
  const name = /^[A-Za-z]+/.exec(s.slice(i + 1, i + 40))?.[0];
  if (!name || !(name in INLINE_ANCHORS)) return null;
  let end = i + 1 + name.length;
  let math = true;
  if (name === "sqrt" && s[end] === "[") {
    const close = s.indexOf("]", end);
    if (close === -1) return null;
    end = close + 1;
  }
  for (let a = 0; a < INLINE_ANCHORS[name]; a++) {
    const groupEnd = braceGroupEnd(s, end);
    if (groupEnd === -1) return null;
    if (!TEXTUAL_COMMANDS.has(name) && !mathLikeArgument(s.slice(end, groupEnd))) math = false;
    end = groupEnd;
  }
  return { end, math, textual: TEXTUAL_COMMANDS.has(name) };
}

/**
 * Wrap LaTeX fragments that sit inside running text without delimiters:
 * "Margin: \frac{12}{16} \times 100 = 75\% of sales" → "Margin: $\frac{12}{16}
 * \times 100 = 75\%$ of sales". Reported 2026-10-02: an answer whose
 * formulas were all bare fragments arrived in Word as raw code.
 *
 * Runs only when the document has no $ math at all (the caller's gate), and
 * never inside code. A run starts at a known command whose arguments look like
 * math ("\frac{3}{4}", not "\frac{numerator}{denominator}") and grows across
 * numbers, operators, brackets, \%, further commands, and a single-letter
 * variable that touches an operator ("x = …"); it stops at the first word.
 * Trailing punctuation and operators are left outside. A run made only of
 * \text{…} is not math. Idempotent: wrapped runs sit inside $…$, which the
 * gate and the span check skip.
 */
function wrapInlineBareMath(text: string): string {
  if (!text.includes("\\")) return text;
  return outsideCodeFences(text, (chunk) => {
    // Display blocks made by the earlier steps (environments, bare formula
    // lines, [ … ] blocks) are already math: never enter them.
    let inDisplay = false;
    return chunk
      .split("\n")
      .map((line) => {
        const fences = (line.match(/(?<!\\)\$\$/g) ?? []).length;
        if (fences > 0) {
          if (fences % 2 === 1) inDisplay = !inDisplay;
          return line;
        }
        if (inDisplay || !line.includes("\\")) return line;
        // In a table row "|" separates cells; elsewhere it may be |x|.
        const bar = /^\s*\|/.test(line) ? "" : "|";
        const tokenRight = new RegExp(`^(?:\\d+(?:[.,]\\d+)*|[()[\\]${bar}]|[+\\-=*/^_<>]|[A-Za-z](?![A-Za-z]))`);
        const tokenLeft = new RegExp(`(?:\\d+(?:[.,]\\d+)*|[()[\\]${bar}]|[+\\-=*/^_<>]|(?<![A-Za-z])[A-Za-z])$`);
        const skip = [...codeSpanRanges(line), ...mathSpanRanges(line)];
        const inSkip = (k: number) => skip.some(([a, b]) => k >= a && k < b);
        let out = "";
        let k = 0;
        // Left growth never crosses the end of the last wrapped run, nor a
        // list bullet, heading mark, or quote mark at the start of the line.
        let floor = /^\s*(?:(?:[-*+]|\d+[.)]|#{1,6}|>)\s+)*/.exec(line)?.[0].length ?? 0;
        while (k < line.length) {
          const anchor = line[k] === "\\" && !inSkip(k) ? readCommand(line, k) : null;
          if (!anchor || !anchor.math || anchor.continuationOnly) {
            out += line[k];
            k++;
            continue;
          }
          // Grow right over math tokens, allowing spaces between them.
          let end = anchor.end;
          let onlyText = anchor.textual;
          for (;;) {
            let p = end;
            while (line[p] === " ") p++;
            const cmd = readCommand(line, p);
            if (cmd && cmd.math && !inSkip(p)) {
              end = cmd.end;
              if (!cmd.textual) onlyText = false;
              continue;
            }
            const m = tokenRight.exec(line.slice(p));
            if (!m || inSkip(p)) break;
            if (/^[A-Za-z]$/.test(m[0])) {
              // A single-letter variable counts only when an operator touches it.
              const after = line.slice(p + 1).trimStart()[0] ?? "";
              const before = line.slice(0, p).trimEnd().slice(-1);
              if (!OPERATOR_CHARS.includes(after) && !OPERATOR_CHARS.includes(before)) break;
            }
            if (/^\d/.test(m[0]) || OPERATOR_CHARS.includes(m[0])) onlyText = false;
            end = p + m[0].length;
          }
          // Grow left the same way over what was already copied to `out`.
          let start = k;
          for (;;) {
            let p = start;
            while (p > 0 && line[p - 1] === " ") p--;
            const m = tokenLeft.exec(line.slice(0, p));
            if (!m || p - m[0].length < floor || inSkip(p - 1)) break;
            if (/^[A-Za-z]$/.test(m[0])) {
              const after = line.slice(p).trimStart()[0] ?? "";
              if (!OPERATOR_CHARS.includes(after)) break;
            }
            start = p - m[0].length;
          }
          // Trim what reads as punctuation or a dangling operator.
          let run = line.slice(start, end);
          const lead = /^[\s=+*/^_<>,.;:]+/.exec(run)?.[0].length ?? 0;
          const trail = /[\s=+\-*/^_<>,.;:]+$/.exec(run)?.[0].length ?? 0;
          const runStart = start + lead;
          const runEnd = end - trail;
          run = line.slice(runStart, runEnd);
          if (onlyText || runEnd <= runStart || runStart > k || LONE_SYMBOL.test(run)) {
            out += line[k];
            k++;
            continue;
          }
          out = out.slice(0, out.length - (k - runStart)) + `$${run}$`;
          k = runEnd;
          floor = runEnd;
        }
        return out;
      })
      .join("\n");
  });
}

/**
 * Rewrite multi-line `$$ … $$` spans so each fence sits on its own line.
 *
 * remark-math only recognises display math when the `$$` fences are alone on
 * their lines (or the whole span is one line); Pandoc's tex_math_dollars is
 * more forgiving. So a paste like
 *
 *     $$ \boxed{ \text{Voltage drop}
 *     I R_1 } $$
 *
 * produced a DOCX whose math was fine while the PREVIEW showed raw red LaTeX
 * (issue #26 — the reporter cross-checked us against QuickLaTeX, which parses
 * it). The preview is the product promise, so normalise to the strict form
 * both parsers accept. Single-line `$$x^2$$` already renders and is left
 * untouched; fenced code is never entered. Idempotent: the normalised form
 * matches the pattern again and rewrites to itself.
 */
function normalizeDisplayMath(text: string): string {
  const FENCE = /^\s*(?:```|~~~)/;
  const out: string[] = [];
  let buffer: string[] = [];
  let inFence = false;

  const flush = () => {
    if (buffer.length === 0) return;
    out.push(
      buffer.join("\n").replace(
        /^[ \t]*\$\$([\s\S]*?)\$\$[ \t]*$/gm,
        (match, inner: string) => {
          const body = inner.trim();
          if (!body) return match;
          // A one-line `$$x$$` alone on its line is display math to every
          // reader but remark-math, which renders it inline-style (small,
          // left-aligned, limits beside the operator) while Word shows a
          // display equation. Put it on its own fences too. Two spans on one
          // line ("$$a$$ and $$b$$") are inline and stay as written.
          if (!match.includes("\n") && body.includes("$")) return match;
          return `$$\n${body}\n$$`;
        },
      ),
    );
    buffer = [];
  };

  for (const line of text.split("\n")) {
    if (FENCE.test(line)) {
      if (!inFence) flush();
      inFence = !inFence;
      out.push(line);
    } else if (inFence) {
      out.push(line);
    } else {
      buffer.push(line);
    }
  }
  flush();
  return out.join("\n");
}

// \left \right and the \big family take a delimiter, never a brace group, so a
// brace right after them can only be a missing backslash: "\left{" → "\left\{".
// "\leftarrow{" and "\bigcup{" have a letter after the name and stay.
const BRACE_DELIMITER = "(?:left|right|[bB]ig{1,2}[lrm]?)";
const OPEN_BRACE_AFTER_DELIMITER = new RegExp(`\\\\(${BRACE_DELIMITER})\\{`, "g");
const CLOSE_BRACE_AFTER_DELIMITER = new RegExp(`\\\\(${BRACE_DELIMITER})\\}`, "g");

/**
 * Escape the brace in "\left{ … \right}" inside math spans. A one-formula
 * document written that way (issue #49, 2026-10-16) rendered as a KaTeX
 * error and reached Word as text: both engines stop at "\left{" ("invalid
 * delimiter"), and a reader cannot see why. Already-escaped "\left\{" has a
 * backslash after the name and does not match. Code is untouched. Idempotent.
 */
function repairDelimiterBraces(text: string): string {
  if (!/\\(?:left|right|[bB]ig)/.test(text)) return text;
  return outsideCodeFences(text, (chunk) =>
    chunk
      .split(/(`+[^`\n]*?`+)/)
      .map((part, i) =>
        i % 2 === 1
          ? part
          : part.replace(MATH_SPAN, (span) =>
              span.replace(OPEN_BRACE_AFTER_DELIMITER, "\\$1\\{").replace(CLOSE_BRACE_AFTER_DELIMITER, "\\$1\\}"),
            ),
      )
      .join(""),
  );
}

/**
 * Put a blank line between a paragraph and the pipe table written straight
 * under it (#57/#58, 2026-10-23: a 35k-character lecture note with nine
 * tables, five of them introduced by a bold line with no blank line after
 * it — "**Step 2 — Greedy allocation:**" and the header on the next line).
 *
 * remark-gfm lets a table interrupt a paragraph, so the preview showed all
 * nine; Pandoc's GFM reader does not, so Word got four tables and five
 * paragraphs full of pipes. The blank line makes both readers agree. The
 * header must start with a pipe and be followed by a delimiter row with the
 * same cell count; the line before must be text — not blank, not a table
 * row, not a delimiter row. Fenced code is never touched. Idempotent: once
 * the blank line is there, the line before is blank.
 */
function separateTableFromParagraph(text: string): string {
  if (!/\n\s*\|?\s*:?-+:?\s*\|/.test(text)) return text;
  return outsideCodeFences(text, (chunk) => {
    const lines = chunk.split("\n");
    const out: string[] = [];
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const prev = out[out.length - 1];
      const next = lines[i + 1];
      if (
        prev !== undefined &&
        prev.trim() !== "" &&
        !prev.trim().startsWith("|") &&
        !(prev.includes("|") && TABLE_DELIMITER_ROW.test(prev)) &&
        line.trim().startsWith("|") &&
        next !== undefined &&
        next.includes("|") &&
        TABLE_DELIMITER_ROW.test(next) &&
        pipeCellCount(line) >= 2 &&
        pipeCellCount(line) === pipeCellCount(next)
      ) {
        out.push("");
      }
      out.push(line);
    }
    return out.join("\n");
  });
}

/**
 * Join pipe-table rows that a chat export broke across lines (#52).
 *
 * Gemini's "export to Docs"-style tables end a cell's text with a blank line
 * and continue the same row on the next line:
 *
 *     | **Claim** | 12345
 *
 *      | **Name** | Example Co.
 *
 *      |
 *
 * GFM reads the first fragment as a row (two cells of four) and the rest as
 * paragraphs with literal pipes, so Word got a two-row table with pipe soup
 * under it. Inside a table — a header and a delimiter row with the same cell
 * count, which fixes the column count — a row that starts with "|" and lacks
 * its closing "|" absorbs the fragments that follow, blank lines skipped and
 * each fragment appended with a space, until it ends with "|". A fragment is
 * a line that starts with "|", holds fewer cells than the table has columns,
 * and is not the header of a new table; a complete row is never absorbed,
 * and a row that never closes is left exactly as written. Fenced code is
 * never touched. Idempotent: a joined row is complete.
 */
const TABLE_DELIMITER_ROW = /^\s*\|?\s*:?-+:?\s*(?:\|\s*:?-+:?\s*)*\|?\s*$/;
const CLOSING_PIPE = /(?<!\\)\|$/;

/**
 * A table written one paragraph per cell — what some rich-text → Markdown
 * converters make of a table copied from a chat page (issue #66, 2026-10-30):
 *
 *     |
 *     Observation
 *
 *      |
 *
 *     Interpretation
 *
 *      |
 *     | --- | --- |
 *     |
 *
 *     A first cell
 *
 *      |
 *     …
 *
 * Neither remark nor Pandoc reads a table there: the preview, the PDF and
 * Word showed pipes between paragraphs. A block that opens with a line
 * holding only "|", alternates cell text with lines holding only "|", and
 * has a delimiter row whose column count equals the header's cells is
 * rebuilt as a pipe table, a cell's lines joined with a space. Anything else
 * — a cell holding a pipe, a row that does not close, a count that does not
 * match, no body row — leaves the block exactly as written. Fenced code is
 * never touched. Idempotent: a rebuilt table has no pipe-only lines.
 */
const PIPE_ONLY = /^[ \t]*\|[ \t]*$/;
function rebuildCellPerParagraphTables(text: string): string {
  if (!/^[ \t]*\|[ \t]*$/m.test(text)) return text;
  return outsideCodeFences(text, (chunk) => {
    const lines = chunk.split("\n");
    const out: string[] = [];
    for (let i = 0; i < lines.length; ) {
      const table = PIPE_ONLY.test(lines[i]) ? readCellPerParagraphTable(lines, i) : null;
      if (!table) {
        out.push(lines[i++]);
        continue;
      }
      if (out.length > 0 && out[out.length - 1].trim()) out.push("");
      out.push(...table.rows);
      i = table.next;
      if (i < lines.length && lines[i].trim()) out.push("");
    }
    return out.join("\n");
  });
}

/** One cell-per-paragraph table from the pipe-only line at `start`: its pipe rows and the line after it, or null. */
function readCellPerParagraphTable(lines: string[], start: number): { rows: string[]; next: number } | null {
  const limit = Math.min(lines.length, start + 4000);
  // Cells up to `count` of them (or up to the delimiter row when count is
  // null), each closed by a pipe-only line; blank lines between are layout.
  const readCells = (from: number, count: number | null) => {
    const cells: string[] = [];
    let cell: string[] = [];
    for (let j = from; j < limit; j++) {
      const t = lines[j].trim();
      if (!t) continue;
      if (t === "|") {
        cells.push(cell.join(" "));
        cell = [];
        if (count !== null && cells.length === count) return { cells, next: j + 1, delimiter: "" };
        continue;
      }
      if (count === null && t.includes("|") && TABLE_DELIMITER_ROW.test(t)) {
        return cell.length ? null : { cells, next: j + 1, delimiter: t };
      }
      if (t.includes("|")) return null; // a pipe inside a cell: not this shape
      cell.push(t);
    }
    return null;
  };
  const head = readCells(start + 1, null);
  if (!head || head.cells.length < 2 || pipeCellCount(head.delimiter) !== head.cells.length) return null;
  const n = head.cells.length;
  const rows = [`| ${head.cells.join(" | ")} |`, head.delimiter];
  let next = head.next;
  for (;;) {
    let k = next;
    while (k < limit && !lines[k].trim()) k++;
    if (k >= limit || !PIPE_ONLY.test(lines[k])) break;
    const row = readCells(k + 1, n);
    if (!row) return null;
    rows.push(`| ${row.cells.join(" | ")} |`);
    next = row.next;
  }
  return rows.length > 2 ? { rows, next } : null;
}

/** Cells in a pipe-table line: unescaped pipes, minus the edge pipes. */
function pipeCellCount(row: string): number {
  const t = row.trim();
  const pipes = (t.match(/(?<!\\)\|/g) ?? []).length;
  if (pipes === 0) return 0;
  let cells = pipes + 1;
  if (t.startsWith("|")) cells--;
  if (CLOSING_PIPE.test(t)) cells--;
  return cells;
}

function joinBrokenTableRows(text: string): string {
  if (!/^\s*\|?\s*:?-+:?\s*\|/m.test(text)) return text;
  return outsideCodeFences(text, (chunk) => {
    const lines = chunk.split("\n");
    const out: string[] = [];
    let columns = 0; // > 0 while inside a table
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      if (columns === 0) {
        const header = out[out.length - 1];
        if (header && header.includes("|") && line.includes("|") && TABLE_DELIMITER_ROW.test(line)) {
          const n = pipeCellCount(header);
          if (n >= 2 && n === pipeCellCount(line)) columns = n;
        }
        out.push(line);
        continue;
      }
      if (!trimmed || !trimmed.startsWith("|")) {
        columns = 0;
        out.push(line);
        continue;
      }
      let row = trimmed;
      let end = i;
      while (!CLOSING_PIPE.test(row) && end - i < 24) {
        let k = end + 1;
        while (k < lines.length && !lines[k].trim()) k++;
        if (k >= lines.length) break;
        const next = lines[k].trim();
        if (!next.startsWith("|") || pipeCellCount(next) >= columns) break;
        const after = lines[k + 1];
        if (after !== undefined && after.includes("|") && TABLE_DELIMITER_ROW.test(after)) break;
        row = `${row} ${next}`;
        end = k;
      }
      if (end > i && CLOSING_PIPE.test(row)) {
        out.push(row);
        i = end;
      } else {
        out.push(line);
      }
    }
    return out.join("\n");
  });
}

/**
 * Convert Unicode box-drawing tables to markdown pipe tables.
 *
 * Terminal-formatted AI answers draw tables like
 *
 *     ┌──────────┬─────────────────┐
 *     │  Field   │     Purpose     │
 *     ├──────────┼─────────────────┤
 *     │ persona  │ Who the agent   │
 *     │          │ is              │
 *     └──────────┴─────────────────┘
 *
 * As markdown those are plain paragraphs: proportional fonts destroy the
 *     alignment and every export shows wrapped glyph soup (issues #27 DOCX,
 * #29 PDF — the same class through two formats). The structure is fully
 * recoverable: ├ frames separate logical rows, │ splits cells, and a row
 * wrapped across several lines is one row whose cell fragments join with a
 * space. The first row group becomes the header, as it is in every table
 * this style produces. Only blocks with a ┌ opening frame and ≥2 columns
 * convert — a single-column box is a callout, not a table, and stays as-is.
 * Fenced code is never touched. Idempotent: output contains no box chars.
 */
function convertBoxTables(text: string): string {
  const FENCE = /^\s*(?:```|~~~)/;
  const BARE_FENCE = /^\s*(?:```|~~~)\s*$/;
  const FRAME = /^\s{0,8}[┌├└][─┬┴┼┐┤┘]*[┐┤┘]?\s*$/;
  const CONTENT = /^\s{0,8}│.*│\s*$/;

  const lines = text.split("\n");
  const out: string[] = [];
  let inFence = false;
  let i = 0;

  const parseBlock = (start: number): { table: string[]; end: number } | null => {
    // Collect the run of frame/content lines beginning at the ┌ frame.
    let j = start + 1;
    const groups: string[][][] = []; // groups -> lines -> cells
    let current: string[][] = [];
    while (j < lines.length && (FRAME.test(lines[j]) || CONTENT.test(lines[j]))) {
      if (FRAME.test(lines[j])) {
        if (current.length) groups.push(current);
        current = [];
        if (/└/.test(lines[j])) { j++; break; }
      } else {
        const inner = lines[j].trim().replace(/^│/, "").replace(/│\s*$/, "");
        current.push(inner.split("│").map((c) => c.trim().replace(/\|/g, "\\|")));
      }
      j++;
    }
    if (current.length) groups.push(current);
    if (groups.length < 1) return null;

    // Merge wrapped lines: one group = one logical row.
    const rows = groups.map((group) => {
      const width = Math.max(...group.map((cells) => cells.length));
      const merged: string[] = [];
      for (let c = 0; c < width; c++) {
        merged.push(
          group.map((cells) => cells[c] ?? "").filter(Boolean).join(" ").trim(),
        );
      }
      return merged;
    });

    const cols = Math.max(...rows.map((r) => r.length));
    if (cols < 2 || rows.length < 2) return null; // a box, not a table

    const pad = (r: string[]) => Array.from({ length: cols }, (_, c) => r[c] ?? "");
    const table = [
      `| ${pad(rows[0]).join(" | ")} |`,
      `|${" --- |".repeat(cols)}`,
      ...rows.slice(1).map((r) => `| ${pad(r).join(" | ")} |`),
    ];
    return { table, end: j };
  };

  // A bare fence whose only content is one box table (#71): a chat model
  // fences the table it drew to keep the columns aligned, not to show code
  // — the cells carry **emphasis** it expects to render. A fence with a
  // language tag ("```text") is a listing and stays as written.
  const fencedBoxTable = (fenceAt: number): { table: string[]; end: number } | null => {
    const marker = lines[fenceAt].trim();
    let j = fenceAt + 1;
    while (j < lines.length && !lines[j].trim()) j++;
    if (j >= lines.length || !/^\s{0,8}┌/.test(lines[j]) || !FRAME.test(lines[j])) return null;
    const block = parseBlock(j);
    if (!block) return null;
    let k = block.end;
    while (k < lines.length && !lines[k].trim()) k++;
    if (k >= lines.length || lines[k].trim() !== marker) return null;
    return { table: block.table, end: k + 1 };
  };

  while (i < lines.length) {
    const line = lines[i];
    if (FENCE.test(line)) {
      const fenced = !inFence && BARE_FENCE.test(line) ? fencedBoxTable(i) : null;
      if (fenced) {
        out.push(...fenced.table);
        i = fenced.end;
        continue;
      }
      inFence = !inFence;
      out.push(line);
      i++;
      continue;
    }
    if (!inFence && /^\s{0,8}┌/.test(line) && FRAME.test(line)) {
      const block = parseBlock(i);
      if (block) {
        out.push(...block.table);
        i = block.end;
        continue;
      }
    }
    out.push(line);
    i++;
  }
  return out.join("\n");
}

/**
 * Insert a space between links that sit directly against each other.
 *
 * AI chats emit citation clusters as `[A](url1)[B](url2)`, which renders as
 * "AB" — one merged word in the preview and in every exported format
 * (reported 2026-08-04 with two adjacent AUTO.RIA citations). Markdown has
 * no styling hook that survives into Word or PowerPoint, so the separation
 * must be a real space in the content; doing it here means the preview and
 * all eight formats agree.
 *
 * Fenced and inline code are left untouched — a code sample containing
 * `](x)[` must stay verbatim.
 */
function spaceAdjacentLinks(text: string): string {
  const FENCE = /^\s*(?:```|~~~)/;
  // A closed inline-link destination followed immediately by the next link
  // or image. Destinations containing spaces/parens are left alone.
  const ADJACENT = /(\]\([^()\s]*\))(?=!?\[)/g;

  let inFence = false;
  return text
    .split("\n")
    .map((line) => {
      if (FENCE.test(line)) {
        inFence = !inFence;
        return line;
      }
      if (inFence) return line;
      // Split on inline-code spans; transform only the non-code parts.
      return line
        .split(/(`+[^`]*`+)/)
        .map((part, i) => (i % 2 === 1 ? part : part.replace(ADJACENT, "$1 ")))
        .join("");
    })
    .join("\n");
}

/**
 * Escape "$"-prefixed currency amounts so they read as literal text. A `$` is
 * treated as currency when it is directly followed by digits (with optional
 * thousands/decimal separators), an optional magnitude suffix (K/M/B/T —
 * "$18K/mo", "$3.5M"), and then a word boundary — e.g. "$5", "$10.99",
 * "$1,000", "$140K". This deliberately does NOT touch "$5x$" or "$5+3$"
 * (real math) or "$100$" (a number wrapped as math), and skips
 * already-escaped "\$".
 *
 * The magnitude suffix matters: an unescaped "$18K" pairs with the next
 * dollar sign in the paragraph and renders half a sentence as broken
 * red math (see the math-currency-magnitude corpus case).
 */
function escapeCurrency(text: string): string {
  // Amount may be followed by whitespace, punctuation, a unit slash ($9.99/mo),
  // a range hyphen ($5-$10), or end-of-line. A "." or "," counts as
  // punctuation only when no digit follows it: otherwise the regex backtracks
  // out of the decimal and "$1.5$" (or "$1,5$") reads as the price "$1"
  // followed by a full stop, and the formula stays raw text (#54).
  const CURRENCY = /(?<!\\)\$(\d[\d,]*(?:\.\d+)?[KMBTkmbt]?)(?=[\s;:!?)\]}/-]|[.,](?!\d)|$)/g;

  // Escape per line so a math span can be recognised within its own line.
  // Code is never touched: a backslash inside a fence or a code span is
  // literal, so escaping a SQL "$1" placeholder there would corrupt it.
  let inFence = false;
  return text
    .split("\n")
    .map((line) => {
      if (/^\s{0,3}(```|~~~)/.test(line)) {
        inFence = !inFence;
        return line;
      }
      if (inFence) return line;
      const spans = [...mathSpanRanges(line), ...codeSpanRanges(line)];
      const leading =
        spans.length === 0
          ? line.replace(CURRENCY, (_m, a) => `\\$${a}`)
          : line.replace(CURRENCY, (match, amount, offset: number) => {
              const protectedRange = spans.some(([s, e]) => offset >= s && offset < e);
              return protectedRange ? match : `\\$${amount}`;
            });
      return escapeTrailingCurrency(leading);
    })
    .join("\n");
}

/**
 * Prices written with the sign after the number — "10$", "40$" — which the
 * `$10` pattern above cannot see. Two of them in a paragraph paired up as
 * inline math: the prose between them became one formula, with its spaces
 * dropped, rendering as a single unbreakable line thousands of pixels wide
 * (reported 2026-10-04: a PDF shrunk to a fifth of the page to fit it).
 *
 * Decided by pairing, the way Pandoc reads `$`: a `$` can open math only when
 * something other than a space or punctuation follows it, and can close an
 * open span only when something other than a space precedes it. A `$` right
 * after a digit that is not closing an open span is a price — unless a letter,
 * backslash, or `{` follows it — and is escaped (a quote after it, as in
 * `35$"`, used to open a bogus span). `$n+1$` and `$100$` stay math; code is
 * untouched.
 */
function escapeTrailingCurrency(line: string): string {
  if (!line.includes("$")) return line;
  const code = codeSpanRanges(line);
  const escapeAt: number[] = [];
  let open = false;
  for (let i = 0; i < line.length; i++) {
    if (line[i] !== "$" || line[i - 1] === "\\") continue;
    if (line[i + 1] === "$" || line[i - 1] === "$") continue; // $$ display fences
    if (code.some(([s, e]) => i >= s && i < e)) continue;
    if (open) {
      if (!/\s/.test(line[i - 1] ?? " ")) open = false; // a valid closer
      continue;
    }
    const next = line[i + 1];
    if (/\d/.test(line[i - 1] ?? "") && !/[A-Za-z\\{]/.test(next ?? "")) {
      escapeAt.push(i); // "10$", "35$\"", "5$." — a price, not an opener
      continue;
    }
    if (next !== undefined && !/[\s.,;:!?)\]}]/.test(next)) open = true;
  }
  let out = line;
  for (const i of escapeAt.reverse()) out = `${out.slice(0, i)}\\${out.slice(i)}`;
  return out;
}

/** Character ranges of inline code spans (`…`) on one line. */
function codeSpanRanges(line: string): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  const SPAN = /(`+)[^`\n]*?\1/g;
  let m: RegExpExecArray | null;
  while ((m = SPAN.exec(line)) !== null) ranges.push([m.index, m.index + m[0].length]);
  return ranges;
}

/**
 * Character ranges on one line that are genuine inline math, so the currency
 * pass leaves their delimiters alone.
 *
 * Without this, math whose first character is a digit is destroyed: in
 * `$2 < \text{min\_sup} (3) \implies$` the opening `$2 ` looks exactly like a
 * price, gets escaped, and the span never pairs — the reader gets raw LaTeX in
 * the preview and in every export. `$5x + 3 = 0$` and `$100$` already survived
 * because a letter or `$` follows the digits; a digit followed by a *space* is
 * the case that breaks.
 */
function mathSpanRanges(line: string): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  const SPAN = /(?<!\\)\$([^$\n]+)\$/g;
  let m: RegExpExecArray | null;
  while ((m = SPAN.exec(line)) !== null) {
    if (looksLikeMathSpan(m[1])) ranges.push([m.index, m.index + m[0].length]);
  }
  return ranges;
}

/**
 * Is this `$…$` payload math rather than prose that happens to span two prices?
 *
 * Deliberately high-precision — a false positive turns "costs $5 and $10" into
 * a bogus formula, which is the bug the currency pass exists to prevent. Only
 * signals that cannot occur between two prices count:
 *
 *  - a LaTeX control sequence (`\text`, `\implies`, `\frac`) — decisive;
 *  - `=` or `^`, which don't appear between two amounts in prose.
 *
 * `_` is excluded on purpose: it is markdown emphasis, so "costs $5 _each_ and
 * $10" would otherwise be misread as math.
 */
function looksLikeMathSpan(content: string): boolean {
  if (!content.trim()) return false;
  // A command, or a TeX spacing symbol ("\ ", "\,", ";", "\!", "\:") —
  // "$8,\ 9,\ 7$" lists numbers and was escaped as two prices (#61).
  if (/\\[a-zA-Z ,;:!]/.test(content)) return true;
  // A digit-led span with no whitespace that ends in a letter, digit, brace
  // or bracket — "47,XXY", "45,X0", "2x", "3n+1" — is a formula, not a
  // price: a price between two dollar signs always has prose, and so a
  // space, after it ("costs $5 and $10"), and a range or ratio of prices
  // ("$10K-$20K", "$5/$10") ends its span with the separator. Issue #62
  // (2026-10-28): karyotypes written $47,XXY$ lost their opening dollar to
  // the currency pass, and the closing one paired with the next escaped
  // dollar into a formula of prose.
  if (content.length <= 24 && !/\s/.test(content) && /^\d[\d,.]*[A-Za-z]/.test(content) && /[A-Za-z0-9})\]]$/.test(content)) return true;
  // A subscript fused to its base ("F_n", "x_{1}") counts like "=" or "^";
  // "_each_" is emphasis and does not match — "$1/F_n$" was a price (#60).
  if (!/[=^]|[A-Za-z0-9}]_[A-Za-z0-9{\\]/.test(content)) return false;
  // "=" between two prices is still prose when prose surrounds it: "$200,
  // ΔTP=0.020 parts/min; a worker, cost $500" was read as one formula and
  // both prices lost their escaping (#57/#58). Three words of three or more
  // letters, or a comma or semicolon followed by a space, do not occur
  // inside a formula that starts with a digit and carries no command.
  if ((content.match(/[A-Za-z]{3,}/g) ?? []).length >= 3) return false;
  return !/[,;]\s/.test(content);
}

/**
 * True if PREPROCESSED text contains genuine $-delimited math.
 *
 * Exported for callers that need to know whether a document's fidelity depends
 * on the math pipeline (e.g. warning that a degraded DOCX export turned
 * equations into plain text). Run it on the output of `preprocessMarkdown`:
 * currency is escaped to `\$` by then, so anything left is real math.
 */
export function containsDollarMath(text: string): boolean {
  return hasDollarMath(text);
}

/** True if the text contains genuine (non-escaped) $-delimited math. */
function hasDollarMath(text: string): boolean {
  const unescaped = text.replace(/\\\$/g, "");
  return unescaped.includes("$$") || /\$[^$]+\$/.test(unescaped);
}

/**
 * Convert block formulas from [ ] to $$
 */
function convertBlockFormulas(text: string): string {
  const lines = text.split('\n');
  const processed: string[] = [];
  let i = 0;

  while (i < lines.length) {
    // Check for block formula start
    if (lines[i].trim() === '[') {
      const blockStart = i;
      let blockEnd = -1;

      // Find matching closing bracket
      for (let j = i + 1; j < lines.length; j++) {
        if (lines[j].trim() === ']') {
          blockEnd = j;
          break;
        }
      }

      // If we found a matching pair with content
      if (blockEnd > blockStart + 1) {
        const content = lines.slice(blockStart + 1, blockEnd);

        // Check if content looks like LaTeX
        const contentStr = content.join('\n');
        if (contentStr.includes('\\') || contentStr.includes('^') || contentStr.includes('_')) {
          processed.push('$$');
          processed.push(...content);
          processed.push('$$');
          i = blockEnd + 1;
          continue;
        }
      }
    }

    processed.push(lines[i]);
    i++;
  }

  return processed.join('\n');
}

/**
 * Convert inline formulas from ( ) to $ $
 */
function convertInlineFormulas(text: string): string {
  const lines = text.split('\n');
  const processed: string[] = [];
  let inBlockFormula = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Track if we're inside a block formula
    if (line.trim() === '$$') {
      inBlockFormula = !inBlockFormula;
      processed.push(line);
      continue;
    }

    // Don't process lines inside block formulas
    if (inBlockFormula) {
      processed.push(line);
      continue;
    }

    // Process inline formulas
    processed.push(processInlineLine(line));
  }

  return processed.join('\n');
}

/**
 * Process a single line for inline formula conversion
 */
function processInlineLine(line: string): string {
  // Skip special lines
  if (line.trim() === '' ||
      line.trim() === '$$' ||
      line.trim() === '[' ||
      line.trim() === ']' ||
      line.trim().startsWith('#') ||
      line.trim().startsWith('```') ||
      line.trim().startsWith('---') ||
      line.trim().startsWith('***')) {
    return line;
  }

  let result = '';
  let i = 0;

  while (i < line.length) {
    // Look for opening parenthesis
    if (line[i] === '(' && (i === 0 || line[i-1] !== '\\')) {
      // Find the matching closing parenthesis
      let depth = 1;
      let j = i + 1;
      let content = '';

      while (j < line.length && depth > 0) {
        // Handle escaped characters
        if (line[j] === '\\' && j + 1 < line.length) {
          content += line[j] + line[j + 1];
          j += 2;
          continue;
        }

        if (line[j] === '(' && (j === 0 || line[j-1] !== '\\')) {
          depth++;
        } else if (line[j] === ')' && (j === 0 || line[j-1] !== '\\')) {
          depth--;
          if (depth === 0) {
            break;
          }
        }

        content += line[j];
        j++;
      }

      // A `(` directly after `]` is a markdown link/image destination —
      // never math, however formula-ish the URL looks. (Real report: a
      // ChatGPT citation URL with underscores, auto_mazda_cx_9_37689113,
      // was read as subscripts and rewritten to $…$, which destroyed the
      // link in every export.)
      const isLinkDestination = i > 0 && line[i - 1] === ']';

      // A bare single-letter variable in a definition: "(v) = velocity".
      // The letter alone is too short for isLikelyFormula (deliberately — in
      // prose "(a)" is an enumerator, not math), but a following "=" makes it
      // a variable being defined, which is the Word-paste style issue #25 is
      // written in ("• (v) = velocity (m/s)"). Only the "=" context
      // converts; "(V) is in m/min" stays literal prose.
      const nextNonSpace = line.slice(j + 1).match(/^\s*(\S)/)?.[1];
      const isDefinedVariable =
        /^[A-Za-z]$/.test(content.trim()) && nextNonSpace === "=";

      // If we found a complete parenthesis pair
      if (
        depth === 0 &&
        !isLinkDestination &&
        (isLikelyFormula(content) || isDefinedVariable)
      ) {
        result += '$' + content.trim() + '$';
        i = j + 1;
        continue;
      }
    }

    result += line[i];
    i++;
  }

  return result;
}

/**
 * Check if content is likely a LaTeX formula
 */
function isLikelyFormula(content: string): boolean {
  const trimmed = content.trim();

  // Empty or too short
  if (!trimmed || trimmed.length < 2) return false;

  // URLs and paths are never formulas, even though slugs are full of the
  // characters this heuristic looks for (_ ^ - /). Guards bare parenthesised
  // links too, not just markdown link destinations.
  if (/^(?:https?:\/\/|mailto:|tel:|ftp:\/\/|www\.|\/|\.{1,2}\/|#)/i.test(trimmed)) {
    return false;
  }

  // A short subscripted variable — (F_c), (t_1), (V_f) — is math even with no
  // operator around it. The shape is strict on purpose: ONE letter, then a
  // 1–3 char subscript, so snake_case words in parens ("(file_name)") never
  // match (their stem is longer than one letter).
  if (/^[A-Za-z]_\{?[A-Za-z0-9]{1,3}\}?$/.test(trimmed)) return true;

  // Must contain LaTeX indicators
  const hasBackslash = /\\[a-zA-Z]+/.test(trimmed);
  const hasSuperscript = /\^/.test(trimmed);
  const hasSubscript = /_/.test(trimmed);
  const hasEquals = /=/.test(trimmed);
  const hasMathOps = /[+\-*/]/.test(trimmed);
  const hasGreek = /\\(alpha|beta|gamma|delta|Delta|theta|lambda|mu|pi|sigma|omega|epsilon|varepsilon|phi|varphi)/.test(trimmed);
  const hasMathFuncs = /\\(sin|cos|tan|log|ln|exp|lim|sum|prod|int|frac|sqrt|mathcal|operatorname)/.test(trimmed);

  // Need strong evidence that this is LaTeX
  return hasBackslash || hasMathFuncs || hasGreek ||
         ((hasSuperscript || hasSubscript) && (hasEquals || hasMathOps));
}

/**
 * Detects if content contains unformatted LaTeX
 */
export function hasUnformattedLatex(markdown: string): boolean {
  // Already has LaTeX delimiters
  if (markdown.includes('$$') || /\$[^$]+\$/.test(markdown)) {
    return false;
  }

  // Check for ChatGPT-style block formulas
  const lines = markdown.split('\n');
  for (let i = 0; i < lines.length - 2; i++) {
    if (lines[i].trim() === '[') {
      for (let j = i + 1; j < lines.length; j++) {
        if (lines[j].trim() === ']') {
          const content = lines.slice(i + 1, j).join(' ');
          if (content.includes('\\') || content.includes('^') || content.includes('_')) {
            return true;
          }
          break;
        }
      }
    }
  }

  // Check for inline formulas
  const inlinePattern = /\(\s*[^()]*(?:\\[a-zA-Z]+|[\^_])[^()]*\s*\)/;
  return inlinePattern.test(markdown);
}